import { isSameDay, isSameWeek, subDays, subWeeks } from "date-fns";

import type { InboxNotificationType } from "src/graphql";
import {
  bundleNotifications,
  type InboxBundle,
  isBundleUnread,
  type OutcomeKind,
} from "src/pages/projects/inbox/inbox-groups";
import {
  type InboxGroup,
  type InboxNotification,
  useWaitingTaskIds,
} from "src/pages/projects/inbox/inbox-queries";
import type { InboxFilters } from "src/pages/projects/inbox/inbox-sections";

export const INBOX_TABS = ["forYou", "activity"] as const;
export type InboxTab = (typeof INBOX_TABS)[number];

// What asks something of the viewer. Everything else is an update on work they follow.
const FOR_YOU_REASONS: ReadonlySet<InboxNotification["reason"]> = new Set([
  "review",
  "mention",
  "reply",
]);
const FOR_YOU_TYPES: readonly InboxNotificationType[] = [
  "review",
  "mention",
  "reply",
];
/** The types the Activity tab shows, and so the only ones its filter offers. */
export const ACTIVITY_TYPES: readonly InboxNotificationType[] = [
  "comment",
  "agent",
  "assignment",
  "status",
  "edit",
];

export function isForYou(notification: Pick<InboxNotification, "reason">) {
  return FOR_YOU_REASONS.has(notification.reason);
}

export const ACTIVITY_GROUPINGS = ["recent", "grouped"] as const;
export type ActivityGrouping = (typeof ACTIVITY_GROUPINGS)[number];

/** What a group's title leads with: the project's own icon, the file type's, a task's, or nothing for a time. */
export type ActivityGroupIcon =
  | { kind: "project"; icon: string | null }
  | { kind: "fileType"; type: string }
  | { kind: "task" }
  | null;

/** One group of the Activity tab, with its rows newest first. */
export type ActivityGroup = {
  id: string;
  title: string;
  icon: ActivityGroupIcon;
  bundles: InboxBundle[];
};

type InboxTabs = {
  forYou: {
    /** Still waiting on the viewer, most urgent first: reviews they owe, then the rest newest first. */
    open: InboxBundle[];
    /** What the viewer has answered, most recently answered first. */
    handled: InboxBundle[];
  };
  activity: ActivityGroup[];
};

/** The inbox's groups split into what asks something of the viewer and what only tells them. */
export function splitInboxTabs({
  needsYou,
  updates,
  waitingTaskIds,
}: {
  needsYou: InboxGroup[];
  updates: InboxGroup[];
  /** The tasks whose review waits on the viewer; undefined while it loads. */
  waitingTaskIds?: ReadonlySet<string>;
}): InboxTabs {
  const waitingGroups = new Set(needsYou.map((group) => group.id));
  const urgency = (bundle: InboxBundle, groupId: string) => {
    const [latest] = bundle.notifications;
    if (latest?.reason !== "review") return 1;
    const taskId = latest.event.task?.id;
    const isWaiting =
      waitingTaskIds && taskId
        ? waitingTaskIds.has(taskId)
        : waitingGroups.has(groupId);
    return isWaiting ? 0 : 1;
  };
  const outcomeOf = (bundle: InboxBundle) =>
    bundleOutcome(bundle, waitingTaskIds);
  const requests = [...needsYou, ...updates]
    .flatMap((group) =>
      bundleNotifications(group.notifications.filter(isForYou)).map(
        (bundle) => ({ bundle, rank: urgency(bundle, group.id) }),
      ),
    )
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        Date.parse(latestAt(b.bundle)) - Date.parse(latestAt(a.bundle)),
    )
    .map(({ bundle }) => bundle);
  const forYou = {
    open: requests.filter((bundle) => !outcomeOf(bundle)),
    handled: requests
      .filter((bundle) => outcomeOf(bundle))
      .sort(
        (a, b) =>
          Date.parse(outcomeOf(b)?.at ?? "") -
          Date.parse(outcomeOf(a)?.at ?? ""),
      ),
  };
  // groupActivity orders the rows itself, so the groups keep the server's order.
  const activity = [...needsYou, ...updates].flatMap((group) => {
    const bundles = bundleNotifications(
      group.notifications.filter((notification) => !isForYou(notification)),
    );
    return bundles.length > 0 ? [projectGroup(group, bundles)] : [];
  });
  return { forYou, activity };
}

/** A server group as the Activity tab shows it: the project, or the file outside any project. */
function projectGroup(
  group: InboxGroup,
  bundles: InboxBundle[],
): ActivityGroup {
  const event = group.notifications[0]?.event;
  if (event?.project) {
    return {
      id: group.id,
      title: event.project.title,
      icon: { kind: "project", icon: event.project.icon ?? null },
      bundles,
    };
  }
  return {
    id: group.id,
    title: event?.file?.name || "Untitled",
    icon: event?.file ? { kind: "fileType", type: event.file.type } : null,
    bundles,
  };
}

/** The Activity tab's rows regrouped by day (today, yesterday, earlier this week, last week, earlier) or by the task, file or project each is on, the newest group first. */
export function groupActivity(
  groups: ActivityGroup[],
  by: ActivityGrouping,
  now: Date,
): ActivityGroup[] {
  const newestFirst = groups
    .flatMap((group) => group.bundles)
    .sort((a, b) => Date.parse(latestAt(b)) - Date.parse(latestAt(a)));
  const regrouped = new Map<string, ActivityGroup>();
  for (const bundle of newestFirst) {
    const key = by === "recent" ? dayGroup(bundle, now) : subjectGroup(bundle);
    const group = regrouped.get(key.id);
    if (group) group.bundles.push(bundle);
    else regrouped.set(key.id, { ...key, bundles: [bundle] });
  }
  return [...regrouped.values()];
}

type GroupKey = Omit<ActivityGroup, "bundles">;

function dayGroup(bundle: InboxBundle, now: Date): GroupKey {
  const at = new Date(latestAt(bundle));
  if (isSameDay(at, now)) return { id: "today", title: "Today", icon: null };
  if (isSameDay(at, subDays(now, 1))) {
    return { id: "yesterday", title: "Yesterday", icon: null };
  }
  if (isSameWeek(at, now)) {
    return { id: "this-week", title: "Earlier this week", icon: null };
  }
  if (isSameWeek(at, subWeeks(now, 1))) {
    return { id: "last-week", title: "Last week", icon: null };
  }
  return { id: "earlier", title: "Earlier", icon: null };
}

function subjectGroup(bundle: InboxBundle): GroupKey {
  const event = bundle.notifications[0]?.event;
  if (event?.task) {
    return {
      id: `task:${event.task.id}`,
      title: event.task.title,
      icon: { kind: "task" },
    };
  }
  if (event?.file) {
    return {
      id: `file:${event.file.id}`,
      title: event.file.name || "Untitled",
      icon: { kind: "fileType", type: event.file.type },
    };
  }
  return {
    id: `project:${event?.project?.id ?? ""}`,
    title: event?.project?.title ?? "Untitled",
    icon: { kind: "project", icon: event?.project?.icon ?? null },
  };
}

/** How a request was settled: the viewer answered it, or its review stopped waiting on them. */
type InboxOutcome = { kind: OutcomeKind; at: string };

/**
 * How a row's newest request was settled, if it was.
 * A review request whose task no longer waits on the viewer, approved by someone else or out of review, is closed as of when it came.
 */
export function bundleOutcome(
  bundle: InboxBundle,
  waitingTaskIds: ReadonlySet<string> | undefined,
): InboxOutcome | null {
  const [latest] = bundle.notifications;
  if (!latest) return null;
  if (latest.response) return latest.response;
  const taskId = latest.event.task?.id;
  if (
    latest.reason === "review" &&
    taskId &&
    waitingTaskIds?.has(taskId) === false
  ) {
    return { kind: "closed", at: latest.createdAt };
  }
  return null;
}

/** How a row's request was settled, against the reviews that wait on the viewer now. */
export function useBundleOutcome(bundle: InboxBundle): InboxOutcome | null {
  return bundleOutcome(bundle, useWaitingTaskIds());
}

function latestAt(bundle: InboxBundle): string {
  return bundle.notifications[0]?.createdAt ?? "";
}

export function unreadBundleCount(bundles: InboxBundle[]): number {
  return bundles.filter(isBundleUnread).length;
}

/** The filters narrowed to one tab, so marking all read on it leaves the other tab alone. Null when the filters keep nothing of the tab. */
export function tabFilters(
  filters: InboxFilters,
  tab: InboxTab,
): InboxFilters | null {
  const tabTypes = tab === "forYou" ? FOR_YOU_TYPES : ACTIVITY_TYPES;
  const types =
    filters.types.length === 0
      ? [...tabTypes]
      : filters.types.filter((type) => tabTypes.includes(type));
  return types.length > 0 ? { ...filters, types } : null;
}
